import { authFetch } from "./auth";
import { getLang, Lang, translate } from "./strings";
import { ApiKeyInfo } from "./types";

export async function listApiKeys(): Promise<ApiKeyInfo[]> {
  const response = await authFetch("/api/keys");
  if (!response.ok) throw new Error((await response.text()) || translate("getKeysFailed"));
  return response.json();
}

export async function createApiKey(input: {
  name: string;
  expiresInHours?: number | null;
  key?: string;
}): Promise<ApiKeyInfo & { key: string }> {
  const body: Record<string, unknown> = { name: input.name };
  if (input.expiresInHours) body.expiresInHours = input.expiresInHours;
  const custom = input.key?.trim();
  if (custom) body.key = custom;
  const response = await authFetch("/api/keys", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error((await response.text()) || translate("createKeyFailed"));
  }
  return response.json();
}

export async function revokeApiKey(id: string) {
  const response = await authFetch(`/api/keys?id=${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error((await response.text()) || translate("revokeKeyFailed"));
}

export function uploadCurlExample(origin: string, apiKey = "<apiKey>", path = "folder/") {
  return `curl -X POST "${origin}/api/upload?path=${path}" -H "Authorization: Bearer ${apiKey}" -F "file=@photo.jpg"`;
}

export function downloadCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  path = "DBX/sync/snapshot.json"
) {
  return `curl -L "${origin}/api/download?path=${path}" -H "Authorization: Bearer ${apiKey}" -o snapshot.json`;
}

export function listCurlExample(origin: string, apiKey = "<apiKey>", path = "folder/") {
  return `curl "${origin}/api/list?path=${path}" -H "Authorization: Bearer ${apiKey}"`;
}

export function overwriteCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  path = "folder/"
) {
  return `curl -X POST "${origin}/api/upload?path=${path}&overwrite=1" -H "Authorization: Bearer ${apiKey}" -F "file=@photo.jpg"`;
}

export function backupCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  path = "folder/notes.txt"
) {
  return `curl -X POST "${origin}/api/backup?path=${path}" -H "Authorization: Bearer ${apiKey}"`;
}

export function deleteCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  path = "folder/notes.txt"
) {
  return `curl -X DELETE "${origin}/api/delete?path=${path}" -H "Authorization: Bearer ${apiKey}"`;
}

export function renameCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  fromPath = "folder/old.txt",
  toPath = "folder/new.txt"
) {
  return `curl -X POST "${origin}/api/rename" -H "Authorization: Bearer ${apiKey}" -H "Content-Type: application/json" -d '{"from":"${fromPath}","to":"${toPath}"}'`;
}

export function mkdirCurlExample(
  origin: string,
  apiKey = "<apiKey>",
  path = "folder/sub"
) {
  return `curl -X POST "${origin}/api/mkdir" -H "Authorization: Bearer ${apiKey}" -H "Content-Type: application/json" -d '{"path":"${path}"}'`;
}

// 调用说明是整段多行文档，按语言各存一份行模板，插值 origin/key 后拼装。
const API_GUIDE: Record<Lang, (ctx: { origin: string; key: string }) => string[]> = {
  zh: ({ origin, key }) => [
    "调用说明",
    "",
    "鉴权（所有开放接口相同，二选一，无需网页登录）：",
    `  Authorization: Bearer ${key}`,
    `  X-Api-Key: ${key}`,
    "内部目录 _$flaredrive$/ 一律拒绝。不使用网页 session。",
    "",
    "—— MCP / Cursor ——",
    "",
    `远程 MCP（Streamable HTTP）：POST ${origin}/mcp`,
    "鉴权同上；v1 工具：list / upload / download / mkdir / delete。",
    "单文件约 1 MiB 上限，更大请用网页端。delete 默认进回收站，hard=true 永久删除。",
    "",
    "Cursor mcp.json：",
    `{`,
    `  "mcpServers": {`,
    `    "davflare": {`,
    `      "url": "${origin}/mcp",`,
    `      "headers": {`,
    `        "Authorization": "Bearer ${key}"`,
    `      }`,
    `    }`,
    `  }`,
    `}`,
    "",
    "—— 上传 ——",
    "",
    `接口：POST ${origin}/api/upload`,
    "查询参数：path=目标目录/ （可空，表示根目录）",
    "  overwrite=1 或 true：按原文件名 PUT 覆盖同路径对象",
    "  默认（不带 overwrite）：同名会 uniqueName 为 name (2).ext，兼容现有客户端",
    "请求体：multipart 字段 file=@本地文件",
    "也可发送原始请求体，并加上 X-File-Name 文件名头。",
    "成功：201，返回 JSON { key, name, size, path, overwritten }",
    "目标是目录：409",
    "密钥无效或已过期：401",
    "单次文件过大（约 100MB）：413",
    "更大的文件请改用网页端分块上传。",
    "",
    "示例（multipart，不覆盖）：",
    `curl -X POST "${origin}/api/upload?path=folder/" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -F "file=@photo.jpg"`,
    "",
    "示例（覆盖上传 overwrite=1）：",
    `curl -X POST "${origin}/api/upload?path=folder/&overwrite=1" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -F "file=@photo.jpg"`,
    "",
    "示例（X-Api-Key + 原始请求体）：",
    `curl -X POST "${origin}/api/upload?path=docs/" \\`,
    `  -H "X-Api-Key: ${key}" \\`,
    `  -H "X-File-Name: notes.txt" \\`,
    `  --data-binary @notes.txt`,
    "",
    "—— 下载 ——",
    "",
    `接口：GET ${origin}/api/download`,
    "查询参数：path=对象键（必填，须为文件，不能是目录）",
    "成功：200，返回文件内容（Content-Disposition: attachment）",
    "缺少 path 或目标是目录：400；不存在：404；密钥无效：401",
    "",
    "示例：",
    `curl -L "${origin}/api/download?path=DBX/sync/snapshot.json" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -o snapshot.json`,
    "",
    "—— 列出目录（Depth 1）——",
    "",
    `接口：GET ${origin}/api/list`,
    "查询参数：path=目录/ （可空，表示根目录）",
    "只返回当前层：文件 + 直接子文件夹，不会递归整桶。",
    "成功：200，JSON { items: [{ key, name, size, isDir, uploaded, etag }] }",
    "  文件始终带 size（字节）、uploaded（ISO；缺省用纪元）和 etag，另有 updated 别名。",
    "  分隔前缀文件夹：isDir 为 true，size 为 0，uploaded 为 null。",
    "path 是文件：400；目录不存在：404",
    "",
    "示例：",
    `curl "${origin}/api/list?path=folder/" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "示例（大目录分页）：",
    `curl "${origin}/api/list?path=folder/&limit=500&cursor=<nextCursor>" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "—— 大文件分块上传（>100MB）——",
    "",
    `① 创建：POST ${origin}/api/upload?uploads&path=<完整文件键>`,
    "   成功：201 { key, uploadId }",
    `② 传块：PUT ${origin}/api/upload?path=<文件键>&uploadId=<id>&partNumber=<n>`,
    "   原始请求体；单块仍受约 100MB 请求限制；成功 200 { partNumber, etag }",
    `③ 完成：POST ${origin}/api/upload?path=<文件键>&uploadId=<id>`,
    "   body JSON { parts: [{ partNumber, etag }] }；成功 200 { key, size, etag }",
    `④ 放弃：DELETE ${origin}/api/upload?path=<文件键>&uploadId=<id> → 204`,
    "",
    "—— 创建目录 ——",
    "",
    `接口：POST ${origin}/api/mkdir`,
    "JSON { path } 或查询参数 path=目录键（也可 X-File-Path 头）。",
    "父目录会自动补建；已存在同名目录幂等返回 200（created: false）；",
    "同名文件占位：409。成功创建：201，JSON { key, created }。",
    "",
    "示例：",
    `curl -X POST "${origin}/api/mkdir" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"path":"folder/sub"}'`,
    "",
    "—— 冲突备份（同步冲突用这个）——",
    "",
    `接口：POST ${origin}/api/backup`,
    "查询参数：path=远端文件键（也可 JSON { path }）",
    "将文件复制到同目录 name.conflict-YYYYMMDDTHHMMSS.ext（保留扩展名，时间戳为 UTC），再删除原键。",
    "例如 notes.txt → notes.conflict-20260828T115537.txt",
    "R2 没有原生 rename，因此是 copy + delete。",
    "成功：200，JSON { from, to }",
    "目录：400；不存在：404",
    "",
    "示例：",
    `curl -X POST "${origin}/api/backup?path=folder/notes.txt" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "—— 重命名 ——",
    "",
    `接口：POST ${origin}/api/rename`,
    "JSON { from, to } 或查询参数 from / to。",
    "文件：复制到 to 再删除 from。to 已存在则 409，除非 overwrite=1。",
    "目录：整树递归移动（不支持 overwrite；目标已存在 409；上限 1000 对象）。",
    "成功：200，JSON { from, to }（目录带 kind: \"directory\"）",
    "",
    "示例：",
    `curl -X POST "${origin}/api/rename" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"from":"folder/old.txt","to":"folder/new.txt"}'`,
    "",
    "—— 删除 ——",
    "",
    `接口：DELETE ${origin}/api/delete`,
    "查询参数：path=文件或目录键。",
    "  默认硬删除；文件直接删，目录递归删除（上限 1000 对象）。",
    "  soft=1：软删除进回收站（可经 /api/trash?action=restore 还原），文件与目录都支持。",
    "成功：200，JSON { key, deleted: true }（目录带 kind，软删带 soft/trashKey）",
    "密钥无效：401；内部目录：400；不存在：404",
    "",
    "示例：",
    `curl -X DELETE "${origin}/api/delete?path=folder/notes.txt" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "示例（软删除进回收站）：",
    `curl -X DELETE "${origin}/api/delete?path=folder/sub&soft=1" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "—— 冲突备份补充 ——",
    "目录备份：整树改名为 name.conflict-<UTC戳>（不拆扩展名）。",
    "",
    "—— 双向同步配方（本地优先，远端冲突先备份）——",
    "",
    "本地 = 用户机器上的文件。冲突策略：保留 LOCAL，先把 REMOTE 改名备份。",
    "1. GET /api/list 列出文件夹；按 key 比较：本地 mtime/size/etag 与远端 uploaded/size/etag。",
    "2. 仅本地新增或已改 → POST /api/upload?overwrite=1",
    "3. 仅远端新增或已改 → GET /api/download",
    "4. 双方都改（冲突）→ POST /api/backup?path=remoteKey",
    "   （远端改为 *.conflict-YYYYMMDDTHHMMSS.*），再 POST /api/upload?overwrite=1",
    "   用本地字节写回原文件名。",
    "5. 本地已删（可选）：DELETE /api/delete。默认配方可跳过删除，除非客户端有同步库跟踪。",
    "6. 远端多出来的文件：下载到本地。",
  ],
  en: ({ origin, key }) => [
    "Usage",
    "",
    "Auth (same for every open API endpoint, either header; no web session):",
    `  Authorization: Bearer ${key}`,
    `  X-Api-Key: ${key}`,
    "The internal prefix _$flaredrive$/ is always rejected. Web sessions are not used.",
    "",
    "— MCP / Cursor —",
    "",
    `Remote MCP (Streamable HTTP): POST ${origin}/mcp`,
    "Same auth as above. v1 tools: list / upload / download / mkdir / delete.",
    "About 1 MiB per file; use the web UI for larger files. delete defaults to trash; hard=true permanently deletes.",
    "",
    "Cursor mcp.json:",
    `{`,
    `  "mcpServers": {`,
    `    "davflare": {`,
    `      "url": "${origin}/mcp",`,
    `      "headers": {`,
    `        "Authorization": "Bearer ${key}"`,
    `      }`,
    `    }`,
    `  }`,
    `}`,
    "",
    "— Upload —",
    "",
    `Endpoint: POST ${origin}/api/upload`,
    "Query: path=target-folder/ (optional; empty means the root folder)",
    "  overwrite=1 or true: PUT over the same object name",
    "  Default (no overwrite): name clashes get uniqued to name (2).ext, matching existing clients",
    "Body: multipart form field file=@local-file",
    "You may also send a raw request body with the X-File-Name header.",
    "Success: 201, JSON { key, name, size, path, overwritten }",
    "Target is a directory: 409",
    "Invalid or expired key: 401",
    "Single file too large (~100MB): 413",
    "For larger files use the web chunked uploader.",
    "",
    "Example (multipart, no overwrite):",
    `curl -X POST "${origin}/api/upload?path=folder/" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -F "file=@photo.jpg"`,
    "",
    "Example (overwrite upload, overwrite=1):",
    `curl -X POST "${origin}/api/upload?path=folder/&overwrite=1" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -F "file=@photo.jpg"`,
    "",
    "Example (X-Api-Key + raw body):",
    `curl -X POST "${origin}/api/upload?path=docs/" \\`,
    `  -H "X-Api-Key: ${key}" \\`,
    `  -H "X-File-Name: notes.txt" \\`,
    `  --data-binary @notes.txt`,
    "",
    "— Download —",
    "",
    `Endpoint: GET ${origin}/api/download`,
    "Query: path=object-key (required; must be a file, not a directory)",
    "Success: 200, returns the file body (Content-Disposition: attachment)",
    "Missing path or directory target: 400; not found: 404; invalid key: 401",
    "",
    "Example:",
    `curl -L "${origin}/api/download?path=DBX/sync/snapshot.json" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -o snapshot.json`,
    "",
    "— List folder (Depth 1) —",
    "",
    `Endpoint: GET ${origin}/api/list`,
    "Query: path=folder/ (optional; empty means the root folder)",
    "Returns the current level only: files plus direct subfolders, never the whole bucket.",
    "Success: 200, JSON { items: [{ key, name, size, isDir, uploaded, etag }] }",
    "  Files always carry size (bytes), uploaded (ISO; epoch when missing) and etag, plus an updated alias.",
    "  Delimiter-prefix folders: isDir true, size 0, uploaded null.",
    "path is a file: 400; folder not found: 404",
    "",
    "Example:",
    `curl "${origin}/api/list?path=folder/" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "Example (paged listing for large folders):",
    `curl "${origin}/api/list?path=folder/&limit=500&cursor=<nextCursor>" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "— Multipart upload for large files (>100MB) —",
    "",
    `1. Create: POST ${origin}/api/upload?uploads&path=<full-file-key>`,
    "   Success: 201 { key, uploadId }",
    `2. Upload part: PUT ${origin}/api/upload?path=<file-key>&uploadId=<id>&partNumber=<n>`,
    "   Raw body; each part still limited to ~100MB per request; success 200 { partNumber, etag }",
    `3. Complete: POST ${origin}/api/upload?path=<file-key>&uploadId=<id>`,
    "   body JSON { parts: [{ partNumber, etag }] }; success 200 { key, size, etag }",
    `4. Abort: DELETE ${origin}/api/upload?path=<file-key>&uploadId=<id> → 204`,
    "",
    "— Create folder —",
    "",
    `Endpoint: POST ${origin}/api/mkdir`,
    "JSON { path } or query path=folder-key (also via the X-File-Path header).",
    "Parent folders are created automatically; an existing folder returns 200 idempotently (created: false);",
    "a file occupying the name: 409. Created: 201, JSON { key, created }.",
    "",
    "Example:",
    `curl -X POST "${origin}/api/mkdir" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"path":"folder/sub"}'`,
    "",
    "— Conflict backup (use this on sync conflicts) —",
    "",
    `Endpoint: POST ${origin}/api/backup`,
    "Query: path=remote-file-key (or JSON { path })",
    "Copies the file to name.conflict-YYYYMMDDTHHMMSS.ext in the same folder (extension kept, UTC stamp), then deletes the original key.",
    "Example: notes.txt → notes.conflict-20260828T115537.txt",
    "R2 has no native rename, so this is copy + delete.",
    "Success: 200, JSON { from, to }",
    "Directory: 400; not found: 404",
    "",
    "Example:",
    `curl -X POST "${origin}/api/backup?path=folder/notes.txt" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "— Rename —",
    "",
    `Endpoint: POST ${origin}/api/rename`,
    "JSON { from, to } or query params from / to.",
    "File: copied to to, then from is deleted. Existing to: 409 unless overwrite=1.",
    "Directory: whole-tree recursive move (no overwrite; existing target 409; 1000-object cap).",
    "Success: 200, JSON { from, to } (directories carry kind: \"directory\")",
    "",
    "Example:",
    `curl -X POST "${origin}/api/rename" \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -d '{"from":"folder/old.txt","to":"folder/new.txt"}'`,
    "",
    "— Delete —",
    "",
    `Endpoint: DELETE ${origin}/api/delete`,
    "Query: path=file-or-folder-key.",
    "  Default is a hard delete; files are removed directly, directories recursively (1000-object cap).",
    "  soft=1: soft-delete into the trash (restorable via /api/trash?action=restore); works for files and folders.",
    "Success: 200, JSON { key, deleted: true } (directories carry kind, soft deletes carry soft/trashKey)",
    "Invalid key: 401; internal folder: 400; not found: 404",
    "",
    "Example:",
    `curl -X DELETE "${origin}/api/delete?path=folder/notes.txt" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "Example (soft-delete into trash):",
    `curl -X DELETE "${origin}/api/delete?path=folder/sub&soft=1" \\`,
    `  -H "Authorization: Bearer ${key}"`,
    "",
    "— Conflict backup notes —",
    "Directory backup: whole tree renamed to name.conflict-<UTC stamp> (extension not split).",
    "",
    "— Two-way sync recipe (local wins; remote conflicts backed up first) —",
    "",
    "Local = files on the user's machine. Conflict policy: keep LOCAL and rename REMOTE to a backup first.",
    "1. GET /api/list to read the folder; compare by key: local mtime/size/etag vs remote uploaded/size/etag.",
    "2. Only local is new or changed → POST /api/upload?overwrite=1",
    "3. Only remote is new or changed → GET /api/download",
    "4. Both changed (conflict) → POST /api/backup?path=remoteKey",
    "   (remote becomes *.conflict-YYYYMMDDTHHMMSS.*), then POST /api/upload?overwrite=1",
    "   writing the local bytes back to the original name.",
    "5. Deleted locally (optional): DELETE /api/delete. The default recipe skips deletes unless the client tracks deletions.",
    "6. Extra remote files: download them locally.",
  ],
};

export function formatApiUsage(
  origin: string,
  apiKey = "<apiKey>",
  options?: { includeMcp?: boolean }
) {
  const key = apiKey || "<apiKey>";
  let text = API_GUIDE[getLang()]({ origin, key }).join("\n");
  if (options?.includeMcp === false) {
    text = text.replace(/\n—— MCP \/ Cursor ——[\s\S]*?(?=\n—— )/, "");
    text = text.replace(/\n— MCP \/ Cursor —[\s\S]*?(?=\n— )/, "");
  }
  return text;
}
